import React, { useState, useEffect, useCallback } from 'react';
import './AdminStudentRegistration.css';
import { fetchWithAuth, isSuperAdmin, getBranchId } from '../../utils/auth';
import { API_URL } from '../../config';
import {
  listAcademicYears, createAcademicYear, setCurrentAcademicYear,
  getYearConfig, upsertYearConfig,
  listGrades, createGrade, updateGrade, deactivateGrade,
  listGradeOfferings, createGradeOffering, updateGradeOfferingBooks, syncBooks,
} from './studentRegistrationApi';

const centsToDollars = (cents) => (cents == null ? '' : (cents / 100).toFixed(2));
const dollarsToCents = (dollars) => Math.round(parseFloat(dollars || '0') * 100);

const AdminStudentRegistrationSetup = () => {
  const superAdmin = isSuperAdmin();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(getBranchId() || 1);

  const [years, setYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState(null);
  const [newYear, setNewYear] = useState('');

  const [feeDollars, setFeeDollars] = useState('');
  const [savingFee, setSavingFee] = useState(false);

  const [grades, setGrades] = useState([]);
  const [newGradeName, setNewGradeName] = useState('');
  // One grade at a time: this id drives the single Name + Book List panel
  // below, instead of a separate table listing every grade with its own
  // inline Rename/Retire controls.
  const [selectedGradeId, setSelectedGradeId] = useState(null);
  const [gradeNameDraft, setGradeNameDraft] = useState('');

  const [offerings, setOfferings] = useState([]);
  const [bookDrafts, setBookDrafts] = useState({}); // { [gradeId]: "Book A\nBook B" }

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncingBooks, setSyncingBooks] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');

  // Shared by every place that refetches the grade list, so the picker
  // always has a valid selection -- defaults to the first active grade,
  // but keeps whatever's currently selected if it still exists at all
  // (active or retired), so e.g. retiring the selected grade doesn't
  // suddenly jump the picker elsewhere.
  const applyGrades = (loadedGrades) => {
    setGrades(loadedGrades);
    setSelectedGradeId(prev => {
      if (prev && loadedGrades.some(g => g.id === prev)) return prev;
      return loadedGrades.find(g => g.is_active)?.id ?? loadedGrades[0]?.id ?? null;
    });
  };

  // Keep the name field in sync with whichever grade is currently selected.
  useEffect(() => {
    const grade = grades.find(g => g.id === selectedGradeId);
    setGradeNameDraft(grade ? grade.name : '');
  }, [selectedGradeId, grades]);

  useEffect(() => {
    if (superAdmin) {
      fetchWithAuth(`${API_URL}/api/admin/branches`)
        .then(r => r.json())
        .then(d => setBranches(d.branches || []))
        .catch(() => {});
    }
  }, [superAdmin]);

  const loadYears = useCallback(async () => {
    const { ok, data } = await listAcademicYears();
    if (ok) {
      setYears(data.academic_years || []);
      const current = (data.academic_years || []).find(y => y.is_current);
      setSelectedYearId(prev => prev || (current ? current.id : (data.academic_years[0]?.id ?? null)));
    }
  }, []);

  useEffect(() => { loadYears(); }, [loadYears]);

  const loadYearScopedData = useCallback(async () => {
    if (!selectedYearId) return;
    setLoading(true);
    setError('');
    try {
      const [feeRes, gradesRes, offeringsRes] = await Promise.all([
        getYearConfig(selectedYearId, branchId),
        listGrades(false),
        listGradeOfferings(selectedYearId, branchId),
      ]);
      if (feeRes.ok) setFeeDollars(centsToDollars(feeRes.data.year_config?.registration_fee_cents));
      else setFeeDollars('');
      if (gradesRes.ok) applyGrades(gradesRes.data.grades || []);
      if (offeringsRes.ok) setOfferings(offeringsRes.data.grade_offerings || []);
    } catch {
      setError('Network error loading setup data.');
    } finally {
      setLoading(false);
    }
  }, [selectedYearId, branchId]);

  useEffect(() => { loadYearScopedData(); }, [loadYearScopedData]);

  const handleCreateYear = async () => {
    if (!newYear.trim()) return;
    const { ok, data } = await createAcademicYear(newYear.trim());
    if (ok) {
      setNewYear('');
      await loadYears();
      setSelectedYearId(data.academic_year.id);
    } else {
      setError(data.error || 'Failed to create academic year.');
    }
  };

  const handleSetCurrent = async () => {
    if (!selectedYearId) return;
    const { ok, data } = await setCurrentAcademicYear(selectedYearId);
    if (ok) await loadYears();
    else setError(data.error || 'Failed to set current year.');
  };

  const handleSaveFee = async () => {
    setSavingFee(true);
    const { ok, data } = await upsertYearConfig(selectedYearId, dollarsToCents(feeDollars), branchId);
    if (!ok) setError(data.error || 'Failed to save fee.');
    setSavingFee(false);
  };

  const handleCreateGrade = async () => {
    if (!newGradeName.trim()) return;
    const { ok, data } = await createGrade(newGradeName.trim());
    if (ok) {
      setNewGradeName('');
      const gradesRes = await listGrades(false);
      if (gradesRes.ok) applyGrades(gradesRes.data.grades || []);
      setSelectedGradeId(data.grade.id); // jump straight to the new grade's book list
    } else {
      setError(data.error || 'Failed to create grade.');
    }
  };

  // Backfills any registration in this branch+year that's missing books
  // it should now have — e.g. students imported/registered before a
  // grade's book list was configured in Setup. Additive only, safe to
  // re-run any time; super_admin only since it's a bulk write.
  const handleSyncBooks = async () => {
    setSyncingBooks(true);
    setError('');
    setSyncMessage('');
    const { ok, data } = await syncBooks(selectedYearId, branchId);
    if (ok) {
      setSyncMessage(
        data.registrations_updated > 0
          ? `Checked ${data.registrations_checked} registration(s) — added ${data.books_added} book(s) across ${data.registrations_updated} registration(s).`
          : `Checked ${data.registrations_checked} registration(s) — nothing was missing.`
      );
    } else {
      setError(data.error || 'Failed to sync book lists.');
    }
    setSyncingBooks(false);
  };

  // One Save button for the selected grade: renames it (if the name
  // changed) and saves its book list together, instead of two separate
  // actions the admin has to remember to do individually.
  const handleSaveGrade = async (grade) => {
    setError('');
    if (gradeNameDraft.trim() && gradeNameDraft.trim() !== grade.name) {
      const { ok, data } = await updateGrade(grade.id, { name: gradeNameDraft.trim() });
      if (!ok) {
        setError(data.error || 'Failed to rename grade.');
        return;
      }
    }
    await handleOfferGrade(grade);
    const gradesRes = await listGrades(false);
    if (gradesRes.ok) applyGrades(gradesRes.data.grades || []);
  };

  const handleDeactivateGrade = async (gradeId, name) => {
    if (!window.confirm(`Retire grade "${name}"? It won't be offered in future years, but past history is kept.`)) return;
    const { ok, data } = await deactivateGrade(gradeId);
    if (ok) {
      const gradesRes = await listGrades(false);
      if (gradesRes.ok) applyGrades(gradesRes.data.grades || []);
    } else {
      setError(data.error || 'Failed to retire grade.');
    }
  };

  const offeringForGrade = (gradeId) => offerings.find(o => o.grade_id === gradeId);

  const handleOfferGrade = async (grade) => {
    const raw = bookDrafts[grade.id] || '';
    const books = raw.split('\n').map(b => b.trim()).filter(Boolean);
    const existing = offeringForGrade(grade.id);
    const result = existing
      ? await updateGradeOfferingBooks(existing.id, books, branchId)
      : await createGradeOffering(selectedYearId, grade.id, books, branchId);
    if (result.ok) {
      const offeringsRes = await listGradeOfferings(selectedYearId, branchId);
      if (offeringsRes.ok) setOfferings(offeringsRes.data.grade_offerings || []);
    } else {
      setError(result.data.error || 'Failed to save book list.');
    }
  };

  const currentYearObj = years.find(y => y.id === selectedYearId);

  return (
    <div className="faq-manager-container">
      <div className="faq-manager-header">
        <div>
          <h2>Student Registration — Setup</h2>
          <p className="faq-manager-subtitle">
            One-time-per-year setup: pick/create the academic year, set the registration fee, and
            build each grade's book list. Grades themselves are managed once and reused every year.
          </p>
        </div>
        <div className="faq-manager-header-right">
          {superAdmin && branches.length > 0 && (
            <select value={branchId} onChange={e => setBranchId(Number(e.target.value))} className="branch-select">
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* Academic year */}
      <div className="form-card">
        <h3 className="faq-form-title">Academic Year</h3>
        <div className="sr-inline-row">
          <select value={selectedYearId || ''} onChange={e => setSelectedYearId(Number(e.target.value))}>
            {years.map(y => (
              <option key={y.id} value={y.id}>{y.year}{y.is_current ? ' (current)' : ''}</option>
            ))}
          </select>
          {currentYearObj && !currentYearObj.is_current && (
            <button className="btn-sm" onClick={handleSetCurrent}>Set as current</button>
          )}
          <input
            type="text" placeholder="e.g. 2027-2028" value={newYear}
            onChange={e => setNewYear(e.target.value)} style={{ maxWidth: '160px' }}
          />
          <button className="btn-secondary" onClick={handleCreateYear}>+ Add Year</button>
        </div>
      </div>

      {/* Fee */}
      <div className="form-card">
        <h3 className="faq-form-title">Registration Fee for {currentYearObj?.year || 'this year'}</h3>
        <div className="sr-inline-row">
          <span>$</span>
          <input
            type="number" step="0.01" min="0" value={feeDollars}
            onChange={e => setFeeDollars(e.target.value)} style={{ maxWidth: '120px' }}
          />
          <button className="btn-primary" onClick={handleSaveFee} disabled={savingFee}>
            {savingFee ? 'Saving…' : 'Save Fee'}
          </button>
        </div>
      </div>

      {/* Grades + Book Lists — one grade edited at a time via the dropdown
          (name, status, and its book list for the selected year together),
          instead of a table of every grade with its own inline
          Rename/Retire plus a second, separate book-list picker below it. */}
      <div className="form-card">
        <h3 className="faq-form-title">Grades</h3>
        <div className="sr-inline-row">
          <input
            type="text" placeholder="e.g. Grade 3" value={newGradeName}
            onChange={e => setNewGradeName(e.target.value)}
          />
          <button className="btn-secondary" onClick={handleCreateGrade}>+ Add Grade</button>
          {superAdmin && (
            <button className="btn-secondary" onClick={handleSyncBooks} disabled={syncingBooks}>
              {syncingBooks ? 'Syncing…' : "Sync Students' Book List"}
            </button>
          )}
        </div>
        {syncMessage && <p style={{ fontSize: '13px', color: '#166534', marginTop: '8px' }}>{syncMessage}</p>}

        {loading ? <p style={{ color: '#64748b', marginTop: '12px' }}>Loading…</p> : grades.length === 0 ? (
          <p style={{ color: '#94a3b8', marginTop: '12px' }}>Add a grade above first.</p>
        ) : (
          <>
            <div className="sr-inline-row" style={{ marginTop: '12px' }}>
              <label style={{ fontWeight: 600 }}>Grade</label>
              <select value={selectedGradeId || ''} onChange={e => setSelectedGradeId(Number(e.target.value))}>
                {grades.map(g => <option key={g.id} value={g.id}>{g.name}{!g.is_active ? ' (Retired)' : ''}</option>)}
              </select>
            </div>
            {(() => {
              const grade = grades.find(g => g.id === selectedGradeId);
              if (!grade) return null;
              const offering = offeringForGrade(grade.id);
              const draft = bookDrafts[grade.id] !== undefined
                ? bookDrafts[grade.id]
                : (offering ? offering.books.map(b => b.book_name).join('\n') : '');
              return (
                <div className="sr-grade-book-block" style={{ marginTop: '12px' }}>
                  <div className="form-row faq-form-row">
                    <label>Name</label>
                    <input
                      type="text" value={gradeNameDraft} style={{ maxWidth: '200px' }}
                      onChange={e => setGradeNameDraft(e.target.value)}
                    />
                  </div>
                  <div className="form-row faq-form-row">
                    <label>Status</label>
                    <span>{grade.is_active ? 'Active' : 'Retired'}</span>
                  </div>

                  <label style={{ display: 'block', fontWeight: 600, marginTop: '12px' }}>
                    Book List for {currentYearObj?.year || 'this year'}
                    {!offering && <span className="sr-badge-muted"> — not offered this year yet</span>}
                  </label>
                  <textarea
                    rows={6}
                    placeholder="One book per line"
                    value={draft}
                    onChange={e => setBookDrafts(d => ({ ...d, [grade.id]: e.target.value }))}
                  />

                  <div className="form-actions">
                    <button className="btn-primary" onClick={() => handleSaveGrade(grade)}>Save</button>
                    {grade.is_active && (
                      <button className="btn-sm btn-danger" onClick={() => handleDeactivateGrade(grade.id, grade.name)}>Retire</button>
                    )}
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminStudentRegistrationSetup;
